import {
  useClearRefinements,
  useCurrentRefinements,
  useRefinementList,
} from 'react-instantsearch';
import Dropdown, {
  Footer,
  List,
} from '@monofront/design-system/components/Dropdown';
import { ALGOLIA_FACET_VALUES, ALGOLIA_GRADE_FACET_REGEX, ALGOLIA_INSIGHTS_EVENT_NAME } from '@monofront/business/constants';
import { AlgoliaFacets } from '@monofront/types';

type GradeFilterProps = {
  attribute: AlgoliaFacets;
  title: string;
  isToggled?: boolean;
};

const GradeFilter: React.FC<GradeFilterProps> = ({ attribute, title }) => {
  const {
    items: availableRefinements,
    refine,
    sendEvent,
  } = useRefinementList({
    attribute,
  });
  const { items: currentRefinements } = useCurrentRefinements({
    includedAttributes: [attribute],
  });
  const { refine: clearRefinement } = useClearRefinements({
    includedAttributes: [attribute],
  });

  // Keep only letter scores (A to F and special A+)
  const usableItems = availableRefinements.filter(
    ({ label }) => label.match(ALGOLIA_GRADE_FACET_REGEX),
  );

  // Sort alphabetically, with A+ first
  usableItems.sort((a, b) => {
    if (a.label.toLowerCase() === "a-plus") return -1;
    if (b.label.toLowerCase() === "a-plus") return 1;

    return a.label.localeCompare(b.label);
  });

  if (usableItems.length === 0) {
    return null;
  }

  return (
    <Dropdown
      label={title}
      selectedItemsCount={currentRefinements.length}
      onReset={clearRefinement}
      dropdownItems={usableItems.map((item) => ({
        id: item.value,
        children: (
          <>
            <span
              className={`
              tw-overflow-hidden
              tw-text-ellipsis
              tw-whitespace-nowrap
            `}
              title={item.label}
            >
            {ALGOLIA_FACET_VALUES[item.label] ? ALGOLIA_FACET_VALUES[item.label] : item.label}
            </span>
            <span
              className={`
              tw-lf-counter-badge
              tw-lf-counter-badge-ghost
              tw-ml-1
              tw-mr-4
            `}
            >
              {`(${item.count})`}
            </span>
          </>
        ),
        defaultChecked: item.isRefined,
        onItemClick: (event) => {
          event.preventDefault();
          refine(item.value);

          if (item.value.length > 0) {
            // Not in the doc™️, but `click` forwards a `clickedFilters` event to insights
            // See instantsearch.js/src/lib/utils/createSendEventForFacet.ts
            sendEvent(
              'click',
              item.value,
              ALGOLIA_INSIGHTS_EVENT_NAME.FILTER_CLICKED,
            );
          }
        },
      }))}
    >
      <List />
      <Footer />
    </Dropdown>
  );
};

export default GradeFilter;
